import React, { useState, useCallback } from 'react'
import { View, ScrollView, StyleSheet, TouchableOpacity } from 'react-native'

import { useNavigation, RouteProp, useRoute } from '@react-navigation/native'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { PdfThumbnail } from './components/PdfThumbnail'
import {
  useGetEBookQuery,
  useRequestEbookPrintoutMutation,
} from '../../api/types'
import Loading from '../../components/Loading'
import Separator from '../../components/Separator'
import { StyledPressable } from '../../components/StyledPressable'
import ButtonText from '../../components/Text/ButtonText'
import Typography, { TypographyInput } from '../../components/Text/Typography'
import TouchableSvg from '../../components/TouchableSvg'
import WebModal from '../../components/WebModal'
import loggingCore from '../../core/logging-core'
import useSafeAreaPaddedStyle, {
  headerOptions,
} from '../../hooks/useSafeAreaPaddedStyle'
import { useBanner } from '../../providers/BannerProvider'
import { useWebLayout } from '../../providers/WebLayoutProvider'
import appStyles from '../../styles/app-styles'
import Colors from '../../styles/Colors'
import {
  MainStackParamsType,
  MainStackNavigationType,
} from '../../types/navigation-types'
import { convertHexToRGBA } from '../../utils/colors'

export default function EBookRequestPrintoutScreen(): JSX.Element {
  const insets = useSafeAreaInsets()
  const { params } =
    useRoute<RouteProp<MainStackParamsType, 'EBookRequestPrintout'>>()
  const { goBack } =
    useNavigation<MainStackNavigationType<'EBookRequestPrintout'>>()
  const { t, i18n } = useTranslation(undefined, {
    keyPrefix: 'screens.ebookRequestPrintout',
  })

  const { webLayoutEnabled } = useWebLayout()

  const { data, loading } = useGetEBookQuery({
    variables: { id: params.ebookId },
  })

  const ebook = data?.ebook

  const showBanner = useBanner()
  const [requestPrintoutMutation, { loading: requesting }] =
    useRequestEbookPrintoutMutation()

  const [count, setCount] = useState<number | ''>(1)
  const numberOfCopies = count || 1

  const onRequestPress = useCallback(() => {
    if (requesting) return

    requestPrintoutMutation({
      variables: { id: params.ebookId, numberOfCopies },
    })
      .then(() => {
        showBanner({
          timeoutMs: 5000,
          icon: 'check-filled',
          color: 'brand.action',
          title: t('successBannerTitle'),
          description: t('successBannerDescription'),
        })
        goBack()
      })
      .catch(loggingCore.error)
  }, [
    t,
    goBack,
    requesting,
    showBanner,
    numberOfCopies,
    params.ebookId,
    requestPrintoutMutation,
  ])

  const onCountMinusPress = useCallback(
    () => setCount(current => Math.max((current || 1) - 1, 1)),
    [],
  )
  const onCountPlusPress = useCallback(
    () => setCount(current => (current || 1) + 1),
    [],
  )

  const onCountTextInputChange = useCallback((input: string) => {
    if (!input) return setCount('')
    const number = parseInt(input, 10)
    if (!isNaN(number) && String(number) === input) {
      setCount(number)
    }
  }, [])

  const onCountTextInputBlur = useCallback(
    () => setCount(number => (!number ? 1 : Math.max(number, 1))),
    [],
  )

  // NOTE: Only extracted to fix eslint react/jsx-max-depth
  const renderCountInput = useCallback(
    () => (
      <View style={styles.numberInput}>
        <StyledPressable
          hoverColor={hoverColor}
          pressedColor={pressedColor}
          style={styles.numberInputButton}
          disabledStyle={styles.numberInputButtonDisabled}
          disabled={numberOfCopies < 2}
          onPress={onCountMinusPress}>
          <Typography selectable={false} style={styles.numberInputButtonText}>
            -
          </Typography>
        </StyledPressable>
        <TypographyInput
          maxLength={5}
          inputMode="numeric"
          style={[
            styles.numberInputTextInput,
            numberOfCopies > 99 && {
              width:
                styles.numberInputTextInput.width +
                Math.max(String(numberOfCopies).length - 2, 0) * 8,
            },
          ]}
          value={String(count)}
          onChangeText={onCountTextInputChange}
          onBlur={onCountTextInputBlur}
        />
        <StyledPressable
          hoverColor={hoverColor}
          pressedColor={pressedColor}
          style={styles.numberInputButton}
          onPress={onCountPlusPress}>
          <Typography selectable={false} style={styles.numberInputButtonText}>
            +
          </Typography>
        </StyledPressable>
      </View>
    ),
    [
      count,
      numberOfCopies,
      onCountPlusPress,
      onCountMinusPress,
      onCountTextInputBlur,
      onCountTextInputChange,
    ],
  )

  const renderPreview = useCallback(() => {
    return (
      <View style={styles.preview}>
        <View style={styles.previewPage2} />
        <View style={styles.previewThumbnailHolder}>
          {!!ebook?.fullFileUrl && (
            <PdfThumbnail
              page={1}
              pdfUrl={ebook?.fullFileUrl}
              backgroundColor={Colors['layout.gray']}
            />
          )}
        </View>
      </View>
    )
  }, [ebook])

  // NOTE: Only extracted to fix eslint react/jsx-max-depth
  const renderButtons = useCallback(
    () => (
      <View style={styles.buttonContainer}>
        <TouchableOpacity
          disabled={requesting}
          style={styles.requestButton}
          onPress={onRequestPress}>
          <ButtonText size="medium" color="layout.white">
            {t('requestButton')}
          </ButtonText>
        </TouchableOpacity>
        <Separator width={18} />
        <TouchableOpacity style={styles.cancelButton} onPress={goBack}>
          <ButtonText size="medium" color="layout.black">
            {i18n.t('common.cancel')}
          </ButtonText>
        </TouchableOpacity>
      </View>
    ),
    [t, i18n, goBack, requesting, onRequestPress],
  )

  return (
    <WebModal>
      <View style={styles.container}>
        <Loading loading={loading} />
        <Loading blocking loading={requesting} />
        <View style={[useSafeAreaPaddedStyle(styles.header, headerOptions)]}>
          <TouchableSvg name="back" color="layout.dark" onPress={goBack} />
          {!webLayoutEnabled && (
            <>
              <Separator width={5} />
              <Typography weight="medium" style={styles.headerTitle}>
                {t('title')}
              </Typography>
              <Separator width={45} />
            </>
          )}
        </View>
        <View style={[appStyles.fullSize, webLayoutEnabled && appStyles.row]}>
          {webLayoutEnabled && (
            <View style={styles.previewWebContainer}>{renderPreview()}</View>
          )}
          <ScrollView
            contentContainerStyle={styles.scrollContentContainer}
            style={appStyles.fullSize}>
            <Separator height={32} />
            {webLayoutEnabled ? (
              <Typography weight="bold" style={styles.titleText}>
                {t('title')}
              </Typography>
            ) : (
              <View style={styles.previewMobileContainer}>
                {renderPreview()}
              </View>
            )}
            <Separator height={30} />
            <Typography>
              {t('details.pageCount', { pageCount: ebook?.pageCount ?? '' })}
            </Typography>
            <Separator height={10} />
            <Typography>{t('details.covers')}</Typography>
            <Separator height={10} />
            <Typography>{t('details.dimensions')}</Typography>
            <Separator height={20} />
            <View style={appStyles.inlineContainer}>
              <Typography>{t('numberOfCopiesLabel')}</Typography>
              <Separator width={10} />
              {renderCountInput()}
            </View>
            <Separator height={30} />
            {renderButtons()}
            <Separator height={insets.bottom + 20} />
          </ScrollView>
        </View>
      </View>
    </WebModal>
  )
}
const hoverColor = convertHexToRGBA(Colors['layout.gray'], 0.1)
const pressedColor = convertHexToRGBA(Colors['layout.gray'], 0.3)
const borderColor = convertHexToRGBA(Colors['layout.gray'], 0.5)
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors['layout.white'],
  },
  header: {
    borderColor,
    paddingTop: 15,
    paddingBottom: 16,
    borderBottomWidth: 1,
    paddingHorizontal: 24,
    ...appStyles.inlineContainer,
    justifyContent: 'space-between',
    backgroundColor: Colors['layout.white'],
  },
  headerTitle: {
    flex: 1,
    fontSize: 16,
    textAlign: 'center',
  },
  titleText: { fontSize: 26 },
  scrollContentContainer: {
    flexGrow: 1,
    paddingHorizontal: 25,
  },
  previewWebContainer: {
    minHeight: 550,
    paddingTop: 32,
    paddingLeft: 53,
    paddingRight: 32,
    paddingBottom: 150,
  },
  previewMobileContainer: {
    ...appStyles.horizontalCenter,
    width: '100%',
    aspectRatio: 1,
  },
  preview: { height: '100%' },
  previewPage2: {
    right: -15,
    bottom: -5,
    height: '100%',
    borderRadius: 10,
    position: 'absolute',
    aspectRatio: Math.SQRT1_2,
    backgroundColor: borderColor,
  },
  previewThumbnailHolder: {
    height: '100%',
    borderRadius: 10,
    overflow: 'hidden',
    aspectRatio: Math.SQRT1_2,
  },
  numberInput: {
    ...appStyles.row,
    height: 42,
    borderColor,
    borderWidth: 1,
    borderRadius: 10,
  },
  numberInputButton: {
    ...appStyles.center,
    height: '100%',
    aspectRatio: 1,
  },
  numberInputButtonDisabled: { opacity: 0.2 },
  numberInputButtonText: { fontSize: 20 },
  numberInputTextInput: {
    width: 32,
    height: '100%',
    textAlign: 'center',
  },
  buttonContainer: {
    ...appStyles.row,
    width: '100%',
    maxWidth: 350,
  },
  requestButton: {
    ...appStyles.center,
    flex: 2,
    padding: 15,
    borderRadius: 20,
    backgroundColor: Colors['layout.dark'],
  },
  cancelButton: {
    ...appStyles.center,
    ...appStyles.heavyShadow,
    flex: 1,
    padding: 15,
    borderRadius: 20,
    backgroundColor: Colors['layout.white'],
  },
})
